import type { Editor } from '@tiptap/core';

import { EDITOR_OPERATION_WAIT_TIMEOUT_MS } from '../constants';
import { BLOCK_NODE_NAME } from '../tiptap/nodes/block/constants';
import { INLINE_BLOCK_NODE_NAME } from '../tiptap/nodes/inlineBlock/constants';
import { REFERENCE_NODE_NAME } from '../tiptap/nodes/reference/constants';
import { IMAGE_NODE_NAME } from '../tiptap/nodes/image/constants';
import { isRegistrationSettled } from './isRegistrationSettled.helper';
import { isImagePending } from './isImagePending.helper';

type TPendingOperations = {
  ids: Set<symbol>;
  listeners: Set<() => void>;
  failed: boolean;
};

// Keyed per editor so a late completion cannot release another editor's save.
const operations = new WeakMap<Editor, TPendingOperations>();
const getOperations = (editor: Editor): TPendingOperations => {
  let current = operations.get(editor);
  if (!current) {
    current = { ids: new Set(), listeners: new Set(), failed: false };
    operations.set(editor, current);
  }
  return current;
};

/** The operation includes its cache/editor updates and any rollback, not just the HTTP request. */
export const runPendingEditorOperation = async (editor: Editor, operation: () => Promise<void>): Promise<void> => {
  const current = getOperations(editor);
  const id = Symbol();
  current.ids.add(id);
  current.listeners.forEach((notify) => notify());
  try {
    await operation();
  } catch (error) {
    // Request errors are rolled back inside the operation, so only a broken rollback lands here.
    current.failed = true;
    console.error('Finishing an editor operation failed.', error);
  } finally {
    current.ids.delete(id);
    current.listeners.forEach((notify) => notify());
  }
};

/** Subscribe to the owner for navigation readiness; React need only render when the boolean changes. */
export const subscribePendingEditorOperations = (editor: Editor, notify: () => void): (() => void) => {
  const current = getOperations(editor);
  current.listeners.add(notify);
  editor.on('update', notify);
  return () => {
    current.listeners.delete(notify);
    editor.off('update', notify);
  };
};

/** Set only by a broken rollback, which nothing clears before a reload. */
export const hasFailedEditorOperation = (editor: Editor): boolean => Boolean(operations.get(editor)?.failed);

export const hasPendingEditorOperations = (editor: Editor, documentId: string, requestEdit = true): boolean => {
  const current = operations.get(editor);
  if (current?.ids.size || current?.failed) return true;
  if (editor.isDestroyed || !requestEdit) return false;

  // Node attributes cover the gap before a node view's effect starts its request.
  let pending = false;
  editor.state.doc.descendants((node) => {
    if (pending) return false;
    if ([REFERENCE_NODE_NAME, BLOCK_NODE_NAME, INLINE_BLOCK_NODE_NAME].includes(node.type.name)) {
      pending = !isRegistrationSettled(node.attrs);
    } else if (node.type.name === IMAGE_NODE_NAME) {
      pending = isImagePending(node.attrs, documentId);
    }
  });
  return pending;
};

/** Only explicit saves wait; an autosave must never stall behind a slow upload. */
export const waitForPendingEditorOperations = (editor: Editor, documentId: string, requestEdit = true): Promise<void> =>
  new Promise((resolve, reject) => {
    const current = getOperations(editor);
    const finish = (error?: Error) => {
      clearTimeout(timer);
      unsubscribe();
      editor.off('destroy', onDestroy);
      if (error) reject(error);
      else resolve();
    };
    const check = () => {
      if (editor.isDestroyed || current.failed) {
        finish(new Error('Editor operations could not finish.'));
      } else if (!hasPendingEditorOperations(editor, documentId, requestEdit)) finish();
    };
    // TipTap emits destroy before it destroys the view, so isDestroyed is not true in that event yet.
    const onDestroy = () => finish(new Error('The editor was closed before its operations finished.'));
    const timer = setTimeout(
      () => finish(new Error('Waiting for editor operations timed out.')),
      EDITOR_OPERATION_WAIT_TIMEOUT_MS
    );
    const unsubscribe = subscribePendingEditorOperations(editor, check);
    editor.on('destroy', onDestroy);
    check();
  });
